import { getCustomerByInn, CustomerModel, fetchCustomerModel, saveCustomerModel, fetchCustomerConfig } from '@services/companyApi';
import { createContext, useEffect, useState } from 'react';
import { Button, message, Space, Tabs } from "antd";
import CustomerFinReport from './components/CustomerFinReport';
import { bxGetInnForCurrentCompany } from '@lib/bx24';
import { FormProvider, useForm } from 'react-hook-form';
import { CustomerQuestionnaire } from './components/customerQuestionnaire';
import { useAuthContext } from '@app/authContext';
import { FormRootConfig } from 'types/form';
import { DocumentsWrapper } from '@app/DocumentsBlock/documentsWrapper';
import { useAccurateContext } from '@utils/useAccurateContext';
import styles from "./CompanyTab.module.css";

interface CompanyContextI {
  formConfig: FormRootConfig | null;
  model: CustomerModel | null;
  inn: string | null;
  customerId: string | null;
}

export const CompanyContext = createContext<CompanyContextI | null>(null);
export const useCompanyContext = () => useAccurateContext(CompanyContext);

export default function CompanyTab() {
  const { auth } = useAuthContext();
  const access_token = auth?.access_token ?? "";

  const methods = useForm();

  const [model, setModel] = useState<CustomerModel | null>(null);
  const [config, setConfig] = useState<FormRootConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [inn, setInn] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);

  useEffect(() => {
    if (!access_token) {
      setModel(null);
      return;
    }

    setLoading(true);
    (async () => {
      try {
        const currentInn = await bxGetInnForCurrentCompany();
        setInn(currentInn);
        if (currentInn) {
          const customer = await getCustomerByInn(currentInn, access_token);
          setCustomerId(customer.customerId);
          const model = await fetchCustomerModel(customer.customerId, access_token);
          setModel(model);
          const config = await fetchCustomerConfig(customer.customerId, access_token);
          setConfig(config);
          methods.reset(model.components)
        }
      } catch (e) {
        console.warn('CompanyTab: не удалось получить модель:', e);
      } finally {
        setLoading(false);
      }
    })();

  }, []);

  if (!access_token) return <div className="row muted">Нет подключения</div>;
  if (loading) return <div className="row muted">Загрузка данных...</div>;
  if (!inn) return <div className="row muted">Необходимо указать ИНН</div>;
  if (!model) return <div className="row muted">Не удалось найти анкету для ИНН: {inn}</div>;

  const components = model.components;

  const handleSubmit = methods.handleSubmit(async (data) => {
    if (!customerId) {
      return;
    }

    const payload = {
      components: {
        ...data
      }
    }
    try {
      setSaving(true);
      const newModel = await saveCustomerModel(customerId, payload, access_token);
      setModel(newModel);
      methods.reset(newModel.components);
      message.success("Анкета сохранена");
    } catch (err: any) {
      message.error(`Ошибка при сохранении анкеты: ${err?.message || err}`);
    } finally {
      setSaving(false);
    }
  });

  return (
    <CompanyContext.Provider value={{ formConfig: config, model, inn, customerId }}>
      <FormProvider {...methods}>
        <div className={styles.container}>
          <div className={styles.buttons}>
            <Space>
              <Button type="primary" onClick={handleSubmit} loading={saving} style={{ marginTop: 8 }}>
                Сохранить
              </Button>
            </Space>
          </div>
          <div className={styles.content}>
            <Tabs defaultActiveKey="1" items={[
              {
                key: "1",
                label: "Анкета клиента",
                children: <CustomerQuestionnaire />,
              },
              {
                key: "2",
                label: "Бухгалтерская отчётность",
                children: (
                  <>
                    {components?.customerFinReport && <CustomerFinReport prefix="customerFinReport" />}
                  </>
                ),
              },
              {
                key: "3",
                label: "Документы",
                children: (<DocumentsWrapper prefix="customerDocuments" />),
              },
            ]} />
          </div>
        </div>
      </FormProvider>
    </CompanyContext.Provider>
  );
}
